import { saveAs } from 'file-saver';
import { formatCentsToUsd } from '../../../helpers/formatCentsToUsd';
import { formatDateTimeInVenueTz } from '../../../helpers/formatDateTime';
import { centsToXlsxNumber, applyCurrencyFormat } from '../../../helpers/excelExportHelpers';

/**
 * Export sales report data to XLSX file
 *
 * COLUMN ORDER IS FROZEN. Accounting downloads this sheet to prepare the monthly
 * GE tax return and sorts/audits it by column position. Do not reorder these
 * columns to match the on-screen table or the other reports - the screen may be
 * standardized, this export may not. Any change here needs accounting sign-off
 * first.
 *
 * @param {object} reportData - The report data from the API
 * @param {string} dateFrom - Start date string
 * @param {string} dateTo - End date string
 * @param {boolean} taxBreakout - Whether to include tax breakout columns
 * @param {object} options - Optional flags (e.g. showIntegrationPrice)
 */
export const exportSalesReportToXlsx = async (
    reportData,
    dateFrom,
    dateTo,
    taxBreakout = false,
    options = {},
) => {
    const XLSX = await import('xlsx');
    const fileType =
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const fileExtension = '.xlsx';

    const { showIntegrationPrice = false } = options;

    // Check if any table has unrealised balance data
    const hasUnrealisedBalance = reportData.tables.some(
        (table) =>
            table.summary?.totalUnrealisedBalance ||
            table.items?.some((item) => item.unrealisedBalance || item.unrealisedBalanceRef),
    );

    // Build the data array for the sheet
    const sheetData = [];

    // Add header with date range and total
    sheetData.push(['Sales Report']);
    sheetData.push([
        `Date Range: ${new Date(dateFrom + 'T12:00:00').toLocaleDateString()} - ${new Date(dateTo + 'T12:00:00').toLocaleDateString()}`,
    ]);
    if (taxBreakout && reportData.totalNetAmount !== undefined) {
        sheetData.push([
            `Net Amount: ${formatCentsToUsd(reportData.totalNetAmount || 0)}`,
            `Tax Amount: ${formatCentsToUsd(reportData.totalTaxAmount || 0)}`,
            `Total Amount: ${formatCentsToUsd(reportData.totalAmount || 0)}`,
        ]);
    } else {
        sheetData.push([`Total Amount: ${formatCentsToUsd(reportData.totalAmount || 0)}`]);
    }
    sheetData.push([]); // Empty row

    // Add each table
    reportData.tables.forEach((table, tableIndex) => {
        // Add table header row
        sheetData.push([`${table.showTitle} - ${table.ticketTypeName}`]);

        // Add column headers
        const baseHeaders = [
            'Booking ID',
            'Created At',
            'Booking Status',
            'Contact',
            'Email',
            'Voucher',
            'Customers',
            'Show Date',
        ];
        const amountHeaders = taxBreakout
            ? ['Net Amount', 'Tax Amount', 'Total Amount']
            : ['Amount Due'];
        const headers = [
            ...baseHeaders,
            ...amountHeaders,
            'Processing Fee',
            'Absorbed Processing Fee',
            'Source',
            'Commission Group',
            ...(showIntegrationPrice ? ['Integration Price'] : []),
            ...(hasUnrealisedBalance ? ['Balance'] : []),
        ];
        sheetData.push(headers);

        // Add data rows
        table.items.forEach((item) => {
            const baseRow = [
                item.bookingId,
                formatDateTimeInVenueTz(item.createdAt),
                item.bookingStatus || '',
                item.contact,
                item.email,
                item.voucher,
                item.customers,
                item.availability,
            ];
            const amountRow = taxBreakout
                ? [
                      centsToXlsxNumber(item.netAmount),
                      centsToXlsxNumber(item.taxAmount),
                      centsToXlsxNumber(item.amountDue),
                  ]
                : [centsToXlsxNumber(item.amountDue)];
            const integrationCell = showIntegrationPrice
                ? [centsToXlsxNumber(item.integrationPrice)]
                : [];
            const balanceCell = hasUnrealisedBalance
                ? [item.unrealisedBalanceRef ? '↑' : centsToXlsxNumber(item.unrealisedBalance)]
                : [];
            const row = [
                ...baseRow,
                ...amountRow,
                centsToXlsxNumber(item.processingFee),
                centsToXlsxNumber(item.absorbedProcessingFee),
                item.sourceType === 'affiliate' ? 'Affiliate' : 'Online',
                item.commissionGroupName || '',
                ...integrationCell,
                ...balanceCell,
            ];
            sheetData.push(row);
        });

        // Add summary row
        const baseSum = [
            'Total',
            '',
            '',
            `${table.summary.bookingCount} bookings`,
            '',
            '',
            `${table.summary.customerCount} customers`,
            '',
        ];
        const amountSum = taxBreakout
            ? [
                  centsToXlsxNumber(table.summary.totalNetAmount),
                  centsToXlsxNumber(table.summary.totalTaxAmount),
                  centsToXlsxNumber(table.summary.totalAmount),
              ]
            : [centsToXlsxNumber(table.summary.totalAmount)];
        const integrationSum = showIntegrationPrice
            ? [centsToXlsxNumber(table.summary.totalIntegrationPrice)]
            : [];
        const balanceSum = hasUnrealisedBalance
            ? [centsToXlsxNumber(table.summary.totalUnrealisedBalance)]
            : [];
        const summaryRow = [
            ...baseSum,
            ...amountSum,
            centsToXlsxNumber(table.summary.totalProcessingFees),
            centsToXlsxNumber(table.summary.totalAbsorbedProcessingFees),
            '',
            '',
            ...integrationSum,
            ...balanceSum,
        ];
        sheetData.push(summaryRow);

        // Add empty rows between tables (except for the last one)
        if (tableIndex < reportData.tables.length - 1) {
            sheetData.push([]);
            sheetData.push([]);
        }
    });

    // Create worksheet
    const ws = XLSX.utils.aoa_to_sheet(sheetData);

    // Set column widths
    const baseCols = [
        { wch: 12 }, // Booking ID
        { wch: 20 }, // Created At
        { wch: 15 }, // Booking Status
        { wch: 25 }, // Contact
        { wch: 30 }, // Email
        { wch: 12 }, // Voucher
        { wch: 25 }, // Customers
        { wch: 20 }, // Show Date
    ];
    const amountCols = taxBreakout ? [{ wch: 12 }, { wch: 12 }, { wch: 12 }] : [{ wch: 15 }];
    ws['!cols'] = [
        ...baseCols,
        ...amountCols,
        { wch: 15 }, // Processing Fee
        { wch: 22 }, // Absorbed Processing Fee
        { wch: 10 }, // Source
        { wch: 30 }, // Commission Group
        ...(showIntegrationPrice ? [{ wch: 15 }] : []), // Integration Price
        ...(hasUnrealisedBalance ? [{ wch: 15 }] : []), // Balance
    ];

    // Set currency format for amount, processing fee, integration price, and balance columns
    const amountStart = baseCols.length;
    const amountEnd = amountStart + amountCols.length - 1;
    const processingFeeCol = baseCols.length + amountCols.length;
    const absorbedFeeCol = processingFeeCol + 1;
    const fixedTrailingCols = 2; // Source, Commission Group
    const integrationCol = showIntegrationPrice ? absorbedFeeCol + 1 + fixedTrailingCols : -1;
    const balanceCol = hasUnrealisedBalance
        ? absorbedFeeCol + 1 + fixedTrailingCols + (showIntegrationPrice ? 1 : 0)
        : -1;

    const currencyCols = [];
    for (let col = amountStart; col <= amountEnd; col++) currencyCols.push(col);
    currencyCols.push(processingFeeCol);
    currencyCols.push(absorbedFeeCol);
    if (integrationCol !== -1) currencyCols.push(integrationCol);
    if (balanceCol !== -1) currencyCols.push(balanceCol);

    applyCurrencyFormat(XLSX, ws, sheetData, currencyCols);

    // Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sales Report');

    // Generate file
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const file = new Blob([excelBuffer], { type: fileType });

    // Generate filename with date range
    const filename = `sales-report-${dateFrom}-to-${dateTo}${fileExtension}`;
    saveAs(file, filename);
};

export default exportSalesReportToXlsx;
